import * as React from 'react';
import thousands from 'thousands';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  ColumnValue,
  ColumnValuesSection,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getAsNumber,
  getInitialFieldsFromJsonData,
  valueObject,
  uuidv4,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';
import { hashCode } from 'sos-common/js/utils/hash';

export interface ActivityGoalBudgetProps extends calculationComponentProps {
  columnOneHeading: string;
  columnTwoHeading: string;
  summaryCellId: string;
  activityGoalDescriptionId: string;
  budgetSummaryCellId: string;
  preambleId: string;
}

export interface ActivityGoalBudgetState extends calculationComponentState {
  fields: Field[][];
  sum: number;
  activityGoalLabel: string;
  budgetSummaryValue: number;
  errorMessage: string;
  jsonXmlInput: string;
}

export class ActivityGoalBudget extends React.Component<ActivityGoalBudgetProps, ActivityGoalBudgetState> {
  private _fieldCount: number;
  private _budgetChangedCount: number;
  private _errorId: string;
  private _labelSum = 'Summa';

  constructor(props: ActivityGoalBudgetProps) {
    super(props);
    this._fieldCount = 0;
    this._budgetChangedCount = 0;
    this._errorId = hashCode(props.summaryCellId || props.budgetSummaryCellId, 'error');
    let isValid = true;

    this.state = {
      fields: [],
      sum: 0,
      jsonFields: '',
      activityGoalLabel: '',
      budgetSummaryValue: 0,
      isValid,
      isTouched: false,
      errorMessage: '',
      jsonXmlInput: '',
    };
  }

  componentDidMount() {
    this._addFormIsValidSubscription();
    this._addBudgetSummaryEventSubscription();
    this._addActivityGoalSubscription();
  }

  _addFormIsValidSubscription = () => {
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
  };

  _addBudgetSummaryEventSubscription = () => {
    EventService.subscribe(`cell-updated:${this.props.budgetSummaryCellId}`, (e: CustomEvent) => {
      this.setState(
        {
          budgetSummaryValue: getAsNumber(e.detail),
          isValid: this.state.sum === getAsNumber(e.detail),
          isTouched: this._budgetChangedCount > 0,
        },
        () => {
          this.summarizeFields();
          this._budgetChangedCount++;
        }
      );
    });
  };

  _addActivityGoalSubscription = () => {
    const { activityGoalDescriptionId } = this.props;

    EventService.subscribe(`ActivityGoalLabel:${activityGoalDescriptionId}`, (e: CustomEvent) => {
      this.setState(
        {
          activityGoalLabel: e.detail,
        },
        () => {
          EventService.subscribe(`ActivityGoalModel:${activityGoalDescriptionId}`, (e: CustomEvent) => {
            //FOET-100 / PBI 6307 bug fix:
            //1) A new GUID identifier is used as the synchronization ID for verksamhetsmålsbeskrivning and verksamhetsmålsbudget components.
            //2) The number of items in the  verksamhetsmålsbeskrivning always controls how many items that are shown in the verksamhetsmålsbudget component. So they should never get out of sync any longer.
            let activityGoalIdentfiers = e.detail as string[];
            const databaseFields = this._fillInformationSavedToDatabase();
            this.initializeFieldsFromEvent(activityGoalIdentfiers, databaseFields);
            // Setup a new listener
            EventService.subscribe(
              `ActivityGoalModel:${activityGoalDescriptionId}`,
              (e: CustomEvent) => {
                let activityGoalIdentfiers = e.detail as string[];
                this.updateFieldsFromEvent(activityGoalIdentfiers);
              },
              false
            );
          }).remove();
          this.summarizeFields();
        }
      );
    });
  };

  _fillInformationSavedToDatabase = (): Field[][] => {
    let isValid = true;

    let initialFields: Field[][] = [this.getDefaultEmptyFieldRow()];
    if (this.props.formElementJson) {
      try {
        let calculationComponentModel = JSON.parse(this.props.formElementJson) as calculationComponentModel;
        initialFields = getInitialFieldsFromJsonData(
          calculationComponentModel,
          initialFields,
          this.getDefaultEmptyFieldRow
        );

        let validation = ValidationService.validateComponent(initialFields, this.props.formElementName);
        isValid = validation.componentIsValid;
        initialFields = validation.fieldsClone;
      } catch (error) {
        console.warn('failed to parse JSON:', error);
      }
    }
    this.setState({
      fields: initialFields,
    });
    return initialFields;
  };

  initializeFieldsFromEvent = (activityGoalIdentifiers: string[], databaseFields: Field[][]) => {
    let initialFields: Field[][] = [];

    activityGoalIdentifiers?.forEach((activityGoalIdentifier, index) => {
      initialFields.push(this.getDefaultEmptyFieldRow());

      initialFields[index][0].value = `${this.state.activityGoalLabel} ${index + 1}`; //
      const foundDbField = databaseFields.find((f) => f[2].value == activityGoalIdentifier);
      if (foundDbField) {
        initialFields[index][1].value = foundDbField[1].value; //value that was saved to the database
      }
      initialFields[index][2].value = activityGoalIdentifier;
    });
    this.setState(
      {
        fields: initialFields,
      },
      () => {
        this.summarizeFields();
      }
    );
  };

  updateFieldsFromEvent = (activityGoalIdentfiers: string[]) => {
    const { fields } = this.state;
    let fieldsClone = fields.slice();
    // Subtract
    if (fields.length > activityGoalIdentfiers.length) {
      for (let i = fieldsClone.length - 1; i >= 0; i--) {
        // If I cannot find the ID in fields
        if (activityGoalIdentfiers.find((x) => x === fieldsClone[i][2].value) === undefined) {
          // Remove the entry from fields
          fieldsClone.splice(i, 1);
        }
      }
    }
    // Add
    else if (fields.length < activityGoalIdentfiers.length) {
      activityGoalIdentfiers.forEach((activityGoalIdentfier, i) => {
        if (i >= fields.length) {
          let newField = this.getDefaultEmptyFieldRow();
          newField[2].value = activityGoalIdentfier;
          fieldsClone.push(newField);
        }
      });
    }

    fieldsClone.forEach((fieldArr, index) => {
      fieldArr[0].value = `${this.state.activityGoalLabel} ${index + 1}`;
    });

    this.setState(
      {
        fields: fieldsClone,
      },
      () => {
        this.summarizeFields();
      }
    );
  };

  getSerializedComponent = (fields: Field[][]) => {
    const { columnOneHeading, columnTwoHeading, formElementHeading } = this.props;
    let values = [];
    fields.forEach((field) => {
      values.push([
        valueObject(field[0].value, true),
        valueObject(field[1].value, true),
        valueObject(field[2].value, false),
      ]);
    });
    if (values.length > 0) {
      return serializeComponent(
        [columnOneHeading, columnTwoHeading],
        values,
        ['Summa', thousands(this.state.sum, ' ')],
        formElementHeading
      );
    }
    return '';
  };

  getDefaultEmptyFieldRow = () => {
    const id1 = `activitygoalbudget__${this._fieldCount++}${this.props.formElementName}`;
    const id2 = `activitygoalbudget__${this._fieldCount++}${this.props.formElementName}`;
    const id3 = `activitygoalbudget__${this._fieldCount++}${this.props.formElementName}`;
    return [
      {
        id: id1,
        name: id1,
        value: '',
        isValid: true,
        mandatory: true,
        isReadOnly: true,
        isTouched: false,
      },
      {
        id: id2,
        name: id2,
        value: '0',
        placeholder: 'Ange belopp',
        isValid: true,
        mandatory: true,
        isTouched: false,
        errorMessage: 'Du måste ange ett belopp',
      },
      {
        id: id3,
        name: id3,
        value: '',
        isValid: true,
        mandatory: false,
      },
    ] as Field[];
  };

  validateMe = () => {
    let isValid = true;
    let errorMessage = '';

    // Validate standard field logic
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;
    //if (!isValid) errorMessage = ALL_MANDATORY;

    // Validate business logic
    let sumSameAsBudget = this.state.sum === this.state.budgetSummaryValue;
    if (!sumSameAsBudget) {
      isValid = false;
      errorMessage = `Den totala summan måste vara samma som det sökta beloppet för statsbidraget (${thousands(
        this.state.budgetSummaryValue,
        ' '
      )})`;
    }

    this.setState(
      {
        fields,
        isValid,
        errorMessage,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.formElementName);
      }
    );
  };

  summarizeFields = () => {
    const { formElementName, formElementHeading } = this.props;

    let sum = 0;
    this.state.fields.forEach((fieldArr) => {
      sum += getAsNumber(fieldArr[1].value);
    });

    this.setState(
      {
        sum,
      },
      () => {
        this.validateMe();
        EventService.fire(`cell-updated:${this.props.summaryCellId}`, { detail: this.state.sum });
        this.setState({
          jsonFields: this.getSerializedComponent(this.state.fields),
          jsonXmlInput: getSerializedXmlInputComponent(
            this.state.fields,
            calculationComponentModel2.Create(formElementName, formElementHeading, 'Verksamhetsbudget'),
            this.jsonSeriliazetoMainContentForXmlInput
          ),
        });
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    if (fields.length == 0) throw 'For this component Verksamhetsbudget, there should be at least one field.';

    const columnValues = [] as ColumnValue[];
    const group = { columnValues } as ColumnTypeGroup;
    data.columnTypeGroups.push(group);

    fields.forEach((rowfields) => {
      if (rowfields.length < 2)
        throw 'For this component Verksamhetsbudget, there should be at at least 2 row fields per field';

      const columnValue = { headerValue: rowfields[0].value, value: rowfields[1].value };
      columnValues.push(columnValue);
    });
    columnValues.push({ isSum: true, headerValue: this._labelSum, value: thousands(this.state.sum, ' ') });
  };

  setValue = (field: FormField<any>) => {
    const { activityGoalLabel } = this.state;
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);

    fieldsClone.forEach((fieldArr, index) => {
      fieldArr[0].value = `${activityGoalLabel} ${index + 1}`;
    });

    this.setState(
      {
        fields: fieldsClone,
        //isTouched: true
      },
      () => {
        EventService.fire(`cell-updated:${fieldToUpdate.id}`, { detail: fieldToUpdate.value });
        this.summarizeFields();
      }
    );
  };

  renderRows = (row: Field[], index: number) => {
    const { activityGoalLabel } = this.state;
    let readOnlyClass = row[0].isReadOnly ? 'stb-form-readonly' : '';
    let isLastRow = index === this.state.fields.length - 1;
    readOnlyClass += isLastRow ? ' last' : '';
    this.setAriaErrorObjects(row);

    return (
      <React.Fragment key={`${this.props.summaryCellId}_activitygoalbudgetrow_${row[0].id}`}>
        <div className="autosum__row" role="row">
          <input type="hidden" id={row[2].id} name={row[2].id} value={row[2].value} />
          <div className={`autosum__col1 ${readOnlyClass}`} role="cell">
            <TextInputForPrint
              id={row[0].id}
              classNames={this.shouldShowError(row[0]) ? 'has-error' : ''}
              type="text"
              placeholder={row[0].placeholder}
              value={`${activityGoalLabel} ${index + 1}`}
              onChange={this.setValue}
              fieldName={row[0].name}
              disabled={row[0].isReadOnly}
              onBlur={this.onBlur}
              errorAriaAttributes={row[0].errorAriaAttributes}
              isControlledInput={true}
            />
          </div>

          <div className="autosum__col2" role="cell">
            <TextInputForPrint
              id={row[1].id}
              classNames={`right ${this.shouldShowError(row[1]) ? 'has-error' : ''}`}
              type="text"
              placeholder={row[1].placeholder}
              value={row[1].value}
              onChange={this.setValue}
              fieldName={row[1].name}
              actAsNumber={true}
              isRequired={true}
              onBlur={this.onBlur}
              errorAriaAttributes={row[1].errorAriaAttributes}
            />
          </div>
        </div>
        {row.map((field) => {
          if (this.shouldShowError(field) && field.errorAriaAttributes) {
            return (
              <InputError
                key={`${field.id}_cError`}
                id={field.errorAriaAttributes['aria-describedby']}
                errorText={field.errorMessage}
              />
            );
          }
        })}
      </React.Fragment>
    );
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  onAddMore = (e: React.MouseEvent) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    const addedRow = this.getDefaultEmptyFieldRow();
    fieldsClone.push(addedRow);
    this.setState(
      {
        fields: fieldsClone,
      },
      () => {
        const fieldToFocus = document.getElementById(addedRow[0].id);
        if (fieldToFocus) fieldToFocus.focus();
      }
    );
  };

  public render() {
    const { columnOneHeading, columnTwoHeading, summaryCellId, formElementHeading, preambleId } = this.props;
    const { fields, sum, isValid } = this.state;
    return (
      <div
        className="cc-component"
        role="table"
        aria-label={`${formElementHeading}`}
        aria-describedby={`${preambleId}`}>
        <div role="rowgroup">
          <div className="autosum__row" role="row">
            <div className="autosum__col1" role="columnheader">
              <p className="like-a-label">{columnOneHeading}</p>
            </div>

            <div className="autosum__col2" role="columnheader">
              <p className="like-a-label">{columnTwoHeading}</p>
            </div>
          </div>
        </div>

        <div role="rowgroup">{fields && fields.map(this.renderRows)}</div>

        <div className="autosum__summary" role="row">
          <div className="autosum__col1" role="cell">
            <span className="like-a-label">{this._labelSum}</span>
          </div>
          <div className="autosum__col2" role="cell">
            <input
              className="like-a-label autosum__sumfield"
              id={summaryCellId}
              readOnly={true}
              type="text"
              value={thousands(sum, ' ')}
            />
          </div>
        </div>
        {this.state.errorMessage && <InputError id={this._errorId} errorText={this.state.errorMessage} />}
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
